import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Avatar from "@/components/Avatar";
import PlayerLink from "@/components/PlayerLink";
import WatchBoard from "@/components/WatchBoard";
import { boardShape } from "@/lib/boardShapes";
import { ago, nameOf, resultText, thumbTiles, TURN_LIMIT } from "@/lib/watch";
import { ArrowRight, Eye, MagnifyingGlass, Star, X } from "@phosphor-icons/react";

const POLL_MS = 20000;

function Player({ id, name, elo, side }) {
  const ring = side === 0 ? "border-brand text-brand bg-brand/15" : "border-opp text-opp bg-opp/15";
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <Avatar userId={id} username={name} className={`w-8 h-8 text-xs border-[1.5px] ${ring}`} fallbackClassName="" />
      <PlayerLink id={id} className="font-display font-bold truncate">{name}</PlayerLink>
      {elo != null && <span className="text-xs text-muted-foreground font-semibold">{elo}</span>}
    </div>
  );
}

function GameCard({ g, highlight }) {
  const navigate = useNavigate();
  const live = g.status === "active";
  const mover = nameOf(g, g.turn_user_id);
  return (
    <button
      type="button"
      onClick={() => navigate(`/watch/${g.match_id}`)}
      className="text-left bg-surface border border-border hover:border-foreground/25 transition-colors rounded-2xl p-4 flex gap-4"
      data-testid={`watch-card-${g.match_id}`}
    >
      <WatchBoard
        shape={boardShape(g.layout)}
        tiles={thumbTiles(g.tiles)}
        letters={false}
        finished={g.status === "completed"}
        className="flex-none"
        style={{ width: boardShape(g.layout).cols * 5.6 }}
      />
      <div className="flex-1 min-w-0 flex flex-col gap-2.5 py-0.5">
        <div className="flex items-center justify-between gap-2">
          {highlight ? (
            <span className="text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full bg-amber-400/15 text-amber-600 dark:text-amber-300 flex items-center gap-1.5">
              <Star size={11} weight="fill" /> {g.turn} turns
            </span>
          ) : live ? (
            <span className="text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-400/15 text-emerald-500 dark:text-emerald-300 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Live
            </span>
          ) : (
            <span className={`text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full ${g.win_type === "draw" ? "bg-muted text-muted-foreground" : g.winner_id === g.player2_id ? "bg-opp/15 text-opp" : "bg-brand/15 text-brand"}`}>
              {g.win_type === "draw" ? "Draw" : "Finished"}
            </span>
          )}
          {live && <span className="text-xs text-muted-foreground">Turn {g.turn} of {TURN_LIMIT}</span>}
          {highlight && g.is_bot && <span className="text-xs text-muted-foreground">Bot game</span>}
        </div>
        <Player id={g.player2_id} name={g.player2_username} elo={g.player2_elo} side={1} />
        <Player id={g.player1_id} name={g.player1_username} elo={g.player1_elo} side={0} />
        <div className="mt-auto text-sm">
          <div className="font-bold truncate">{live ? (mover ? `${mover}'s move` : "In play") : resultText(g)}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {live ? `Last move ${ago(g.last_move_at) || "a while ago"}` : `Turn ${g.turn} · ${ago(g.completed_at)}`}
          </div>
        </div>
        <span className={`self-start inline-flex items-center gap-1.5 font-display font-extrabold text-sm rounded-xl px-3.5 py-2 ${live ? "btn-tile bg-brand text-brand-foreground" : "border border-border"}`}>
          {live ? "Watch" : "Replay"} <ArrowRight size={14} weight="bold" />
        </span>
      </div>
    </button>
  );
}

function Section({ title, sub, games, empty, highlight, more }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-xl font-extrabold">{title}</h2>
      {sub && <p className="text-sm text-muted-foreground mt-1">{sub}</p>}
      <div className="mb-3.5" />
      {games.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
          {games.map((g) => <GameCard key={g.match_id} g={g} highlight={highlight} />)}
        </div>
      ) : (
        <div className="border border-dashed border-border rounded-2xl p-8 text-center text-sm text-muted-foreground">{empty}</div>
      )}
      {more}
    </section>
  );
}

const MINE_SHOWN = 6;

export default function Watch() {
  const [data, setData] = useState(null);
  const [mine, setMine] = useState([]);
  const [allMine, setAllMine] = useState(false);

  useEffect(() => {
    api.get("/watch/mine").then(({ data: d }) => setMine(d.games || [])).catch(() => {});
  }, []);
  const [query, setQuery] = useState("");
  const [player, setPlayer] = useState("");

  // Search by username once typing pauses.
  useEffect(() => {
    const t = setTimeout(() => setPlayer(query.trim()), 400);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/watch/games", { params: player ? { player } : {} })
      .then(({ data: d }) => { if (alive) setData({ ...d, player }); })
      .catch(() => { if (alive) setData((d) => d || { live: [], finished: [], player }); });
    load();
    const id = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [player]);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-8 md:py-12" data-testid="watch-page">
      <p className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground mb-2">Community</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Watch games</h1>
      <p className="text-muted-foreground mt-2 max-w-2xl">
        Follow live battles between Jangle players and replay finished ones turn by turn. Players' hands stay hidden.
      </p>

      <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Eye size={16} />
          {data ? `${data.live.length} live · ${data.finished.length} recently finished` : "Loading games…"}
        </div>
        <label className="flex items-center gap-2.5 border border-border rounded-full px-4 h-11 bg-background sm:w-80">
          <MagnifyingGlass size={16} className="text-muted-foreground flex-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a player's games"
            className="flex-1 bg-transparent outline-none text-sm min-w-0"
            data-testid="watch-search"
          />
          {query && (
            <button type="button" aria-label="Clear" onClick={() => setQuery("")} className="text-muted-foreground">
              <X size={14} />
            </button>
          )}
        </label>
      </div>

      {data && (
        <>
          {!data.player && data.highlights?.length > 0 && (
            <Section title="Highlights" sub="The longest battles on Jangle." games={data.highlights} highlight />
          )}
          {!data.player && mine.length > 0 && (
            <Section
              title="Your past games"
              sub="Step back through your finished matches, bot games too."
              games={allMine ? mine : mine.slice(0, MINE_SHOWN)}
              more={!allMine && mine.length > MINE_SHOWN && (
                <button type="button" onClick={() => setAllMine(true)}
                  className="mt-3 text-sm font-bold text-brand hover:underline" data-testid="watch-mine-more">
                  Show all {mine.length}
                </button>
              )}
            />
          )}
          <Section
            title="Live now"
            games={data.live}
            empty={data.player ? `${data.player} has no live games right now.` : "No games are being played right now."}
          />
          <Section
            title="Just finished"
            games={data.finished}
            empty={data.player ? `No finished games found for ${data.player}.` : "No finished games yet."}
          />
        </>
      )}
    </main>
  );
}
